"use client";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import React from "react";

/**
 * Base Shimmer component that creates the shimmer effect
 * Can be customized with different dimensions and styling
 */
export const Shimmer = ({ 
  className, 
  width, 
  height 
}: { 
  className?: string;
  width?: string | number;
  height?: string | number;
}) => (
  <div
    className={cn(
      "relative overflow-hidden bg-gray-200 rounded",
      className
    )}
    style={{ width, height }}
  >
    <motion.div
      className="absolute inset-0 -translate-x-full"
      animate={{
        x: ["0%", "100%"],
        background: [
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)"
        ]
      }}
      transition={{ 
        duration: 1.5, 
        repeat: Infinity,
        ease: "linear" 
      }}
    />
  </div>
);

/**
 * ShimmerText component - creates a shimmer effect for text elements
 * Can render multiple lines with configurable width
 */
export const ShimmerText = ({ 
  lines = 1, 
  width = "100%",
  className
}: { 
  lines?: number;
  width?: string | number | (string | number)[];
  className?: string;
}) => (
  <div className={cn("space-y-2", className)}>
    {Array(lines).fill(0).map((_, i) => {
      const lineWidth = Array.isArray(width) 
        ? width[i] || "100%" 
        : i === lines - 1 && lines > 1 ? "70%" : width;
      
      return (
        <Shimmer 
          key={i}
          className="h-4" 
          width={lineWidth} 
        />
      );
    })}
  </div>
);

/**
 * HeroShimmer component - mimics the Hero component structure
 */
export const HeroShimmer = () => (
  <section className="relative min-h-[80vh] flex items-center">
    <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/60 to-black/30"></div>
    
    <div className="container mx-auto px-4 relative z-10 py-12 md:py-20">
      <div className="max-w-3xl">
        <div>
          <Shimmer className="h-12 mb-4 w-3/4" />
          <ShimmerText lines={2} className="mb-8" />
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Shimmer className="h-12 w-full" />
          <Shimmer className="h-12 w-32" />
        </div>
        
        <div className="flex flex-wrap gap-4 items-center">
          <Shimmer className="h-10 w-32 rounded-full" />
          <Shimmer className="h-10 w-32 rounded-full" />
          <Shimmer className="h-10 w-32 rounded-full" />
        </div>
      </div>
    </div>
  </section>
);

/**
 * CategoryCardShimmer component - mimics a single category card
 */
export const CategoryCardShimmer = () => (
  <div className="overflow-hidden h-full">
    <Shimmer className="h-32 w-full" />
    <div className="p-4">
      <Shimmer className="h-5 w-20 mx-auto mb-2" />
      <Shimmer className="h-3 w-24 mx-auto" />
    </div>
  </div>
);

/**
 * CategoriesShimmer component - mimics the Categories section structure
 */
export const CategoriesShimmer = () => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <Shimmer className="h-8 w-64 mx-auto mb-4" />
        <Shimmer className="h-4 w-full max-w-2xl mx-auto" />
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
        {Array(6).fill(0).map((_, i) => (
          <CategoryCardShimmer key={i} />
        ))}
      </div>
      
      <div className="text-center mt-10">
        <Shimmer className="h-10 w-40 mx-auto rounded-full" />
      </div>
    </div>
  </section>
);

/**
 * RestaurantCardShimmer component - mimics a single restaurant card
 */
export const RestaurantCardShimmer = () => (
  <div className="overflow-hidden h-full">
    <Shimmer className="h-48 w-full" />
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-start">
        <Shimmer className="h-5 w-32" />
        <Shimmer className="h-5 w-8" />
      </div>
      <Shimmer className="h-4 w-40" />
      <div className="flex items-center space-x-4">
        <Shimmer className="h-3 w-16" />
        <Shimmer className="h-3 w-16" />
        <Shimmer className="h-3 w-10" />
      </div>
      <Shimmer className="h-9 w-full" />
    </div>
  </div>
);

/**
 * FeaturedRestaurantsShimmer component - mimics the FeaturedRestaurants section
 */
export const FeaturedRestaurantsShimmer = () => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <Shimmer className="h-8 w-64 mx-auto mb-4" />
        <Shimmer className="h-4 w-full max-w-2xl mx-auto" />
      </div>
      
      <div className="flex justify-center mb-8">
        <Shimmer className="h-10 w-96 rounded-lg" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array(4).fill(0).map((_, i) => (
          <RestaurantCardShimmer key={i} />
        ))}
      </div>
      
      <div className="text-center mt-8">
        <Shimmer className="h-10 w-40 mx-auto rounded-full" />
      </div>
    </div>
  </section>
);
